import { useEffect, useState } from "react";
import { UserPlus, X, Loader2 } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";

const STROKE = 1.5;

/** Recruiter/admin only. Assigns free hiring-manager access to THIS
 * interview only — access is derived from `conducted_by`, nothing else. */
export function InterviewersPanel({ interview, canEdit, onUpdated }) {
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [draftEmails, setDraftEmails] = useState([]);
  const [emailInput, setEmailInput] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!canEdit) return;
    api.get("/team/members").then(({ data }) => setMembers(data)).catch(() => {});
  }, [canEdit]);

  if (!canEdit) return null;

  const nameFor = (userId) => {
    const m = members.find((x) => x.user_id === userId);
    return m ? (m.name || m.email) : userId;
  };

  const currentEmails = (interview?.conducted_by || [])
    .map((uid) => members.find((m) => m.user_id === uid)?.email)
    .filter(Boolean)
    .concat(interview?.pending_interviewer_emails || []);

  const openEditor = () => {
    setDraftEmails(currentEmails);
    setEmailInput("");
    setOpen(true);
  };

  const addEmail = () => {
    const e = emailInput.trim().toLowerCase();
    if (e && !draftEmails.includes(e)) setDraftEmails([...draftEmails, e]);
    setEmailInput("");
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/interviews/${interview.interview_id}`, {
        interviewer_emails: draftEmails,
      });
      toast.success("Interviewers updated");
      setOpen(false);
      onUpdated?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update interviewers");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="hp-card p-5" data-testid="interviewers-panel">
        <div className="flex items-center justify-between">
          <div className="hp-section-label">Interviewers (free seat)</div>
          <button
            onClick={openEditor}
            className="inline-flex items-center gap-1.5 text-[12px] font-medium text-emerald-800 hover:text-emerald-900"
            data-testid="edit-interviewers-button"
          >
            <UserPlus className="h-3.5 w-3.5" strokeWidth={STROKE} /> Assign
          </button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {(interview?.conducted_by || []).map((uid) => (
            <span key={uid} className="rounded-full border border-ink-200 bg-ink-50 px-3 py-1 text-[12px] text-ink-800" data-testid={`interviewer-chip-${uid}`}>
              {nameFor(uid)}
            </span>
          ))}
          {(interview?.pending_interviewer_emails || []).map((e) => (
            <span key={e} className="rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-[12px] text-amber-800" data-testid={`interviewer-pending-${e}`}>
              {e} · pending invite
            </span>
          ))}
          {!interview?.conducted_by?.length && !interview?.pending_interviewer_emails?.length && (
            <span className="text-[13px] text-ink-500">No hiring manager assigned yet</span>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="assign-interviewers-dialog">
          <DialogHeader>
            <DialogTitle>Assign interviewers</DialogTitle>
            <DialogDescription>
              A free hiring-manager seat sees only this interview — no other rounds,
              candidates, or workspace data.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <input
                type="email"
                className="hp-input flex-1"
                placeholder="name@company.com"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
                data-testid="interviewer-email-input"
              />
              <button onClick={addEmail} className="hp-btn-outline" data-testid="add-interviewer-email-button">Add</button>
            </div>
            <div className="flex flex-wrap gap-2">
              {draftEmails.map((e) => (
                <span key={e} className="inline-flex items-center gap-1.5 rounded-full border border-ink-200 bg-ink-50 px-3 py-1 text-[12px] text-ink-800" data-testid={`draft-interviewer-${e}`}>
                  {e}
                  <button onClick={() => setDraftEmails(draftEmails.filter((x) => x !== e))} data-testid={`remove-interviewer-${e}`}>
                    <X className="h-3 w-3" strokeWidth={STROKE} />
                  </button>
                </span>
              ))}
            </div>
            <button onClick={save} disabled={saving} className="hp-btn-primary w-full" data-testid="save-interviewers-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={STROKE} />}
              Save assignments
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
